import React from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';

/**
 * PersonalScoringCriteriaModal - Educational modal explaining the personal assessment scoring criteria
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Handler to close the modal
 */
const PersonalScoringCriteriaModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            Understanding "Clear, Documented, & Transferable"
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Close modal"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6 space-y-6">
          {/* Introduction */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded">
            <p className="text-gray-700 leading-relaxed">
              These three attributes represent the <strong>gold standard</strong> for personal planning, systems,
              and readiness that ensure smooth life transitions. Strong preparation in your personal life protects
              your family and provides peace of mind during exit and beyond.
            </p>
          </div>

          {/* Clear */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <span className="bg-green-100 text-green-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                1
              </span>
              Clear
            </h3>
            <div className="ml-11 space-y-2">
              <p className="text-gray-700">
                <strong>Definition:</strong> Plans, responsibilities, and intentions are unambiguous, easy to understand,
                and clearly communicated to family and advisors.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Defined estate plan with explicit beneficiary designations</li>
                  <li>Clear financial goals and retirement vision</li>
                  <li>Explicit family roles and responsibilities</li>
                  <li>Well-articulated values and legacy intentions</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="font-semibold text-red-800 mb-2">Red Flags (Not Clear):</p>
                <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                  <li>"My family knows what I want" (but nothing is explicitly stated)</li>
                  <li>Vague intentions with no specific plans</li>
                  <li>Family members have different understandings of your wishes</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Documented */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <span className="bg-blue-100 text-blue-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                2
              </span>
              Documented
            </h3>
            <div className="ml-11 space-y-2">
              <p className="text-gray-700">
                <strong>Definition:</strong> Written plans, legal documents, and instructions exist that can be
                referenced, updated, and used by family and advisors.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Executed estate planning documents (wills, trusts, powers of attorney)</li>
                  <li>Written financial plan with investment policy statement</li>
                  <li>Healthcare directives and end-of-life wishes documented</li>
                  <li>Family governance documents or family constitution</li>
                  <li>Asset inventory with account details and access information</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="font-semibold text-red-800 mb-2">Red Flags (Not Documented):</p>
                <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                  <li>"It's all in my head" or "I'll tell my spouse later"</li>
                  <li>No updated will or estate documents</li>
                  <li>Verbal agreements with no legal backing</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Transferable */}
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center">
              <span className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm font-bold">
                3
              </span>
              Transferable
            </h3>
            <div className="ml-11 space-y-2">
              <p className="text-gray-700">
                <strong>Definition:</strong> Responsibilities, knowledge, and relationships can be effectively handed off
                to family members, heirs, or advisors without significant disruption or loss of continuity.
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800 mb-2">Examples:</p>
                <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                  <li>Spouse or trusted family member can access accounts and understand finances</li>
                  <li>Advisory team knows your family and can continue serving them</li>
                  <li>Executors and trustees are prepared and informed</li>
                  <li>Children understand family values, wealth philosophy, and expectations</li>
                  <li>Important relationships are introduced to family members</li>
                </ul>
              </div>
              <div className="bg-red-50 p-4 rounded-lg">
                <p className="font-semibold text-red-800 mb-2">Red Flags (Not Transferable):</p>
                <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                  <li>You're the only one who knows where things are or how things work</li>
                  <li>Advisors don't know your family or long-term intentions</li>
                  <li>Passwords and account access known only to you</li>
                  <li>Family would struggle to manage affairs in your absence</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Why It Matters */}
          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded">
            <h4 className="font-bold text-yellow-900 mb-2">💡 Why This Matters for Your Family</h4>
            <p className="text-yellow-800 text-sm leading-relaxed">
              Clear, documented, and transferable personal planning protects your family and provides peace of mind
              during life transitions. Families with strong preparation experience <strong>significantly less stress
              and conflict</strong> during difficult times. Without proper planning, loved ones often face unnecessary
              confusion, delays, and financial complications when they're most vulnerable.
            </p>
          </div>

          {/* Scoring Guide */}
          <div className="border-t pt-4">
            <h4 className="font-bold text-gray-900 mb-3">Assessment Scoring Scale (0-6):</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center bg-[#c49e73] text-white border-2 border-[#a07d5a]">6</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Exemplary & Optimized</p>
                  <p className="text-xs text-gray-600">Fully clear, comprehensively documented, and seamlessly transferable. You have achieved best-in-class personal preparation with strong systems in place.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-green-100 text-green-800 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">5</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Clear, Documented & Transferable</p>
                  <p className="text-xs text-gray-600">Meets all three core criteria with strong execution. Your personal planning is solid and your family is well-prepared.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">4</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Clear & Documented, Not Yet Transferable</p>
                  <p className="text-xs text-gray-600">Well-defined and properly documented, but your spouse or family may not be fully prepared to step in if needed.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">3</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Clear But Lacking Documentation</p>
                  <p className="text-xs text-gray-600">You understand your situation and have good intentions, but haven't formalized plans in writing or proper legal documents.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">2</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Conceptually Understood, Poorly Defined</p>
                  <p className="text-xs text-gray-600">You recognize the importance of this area but don't have clarity on specifics or a concrete plan. Needs definition and structure.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-red-100 text-red-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">1</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Unfamiliar or Absent</p>
                  <p className="text-xs text-gray-600">This area is not currently addressed in your personal planning, or you're unclear on what the question is asking. Represents a significant gap requiring immediate attention.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg font-bold text-sm min-w-[40px] text-center">0</div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Not Applicable (N/A)</p>
                  <p className="text-xs text-gray-600">This question does not apply to your situation or family structure. These responses are excluded from your final score calculation.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition min-h-[44px]"
          >
            Got it, thanks!
          </button>
        </div>
      </div>
    </div>
  );
};

PersonalScoringCriteriaModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

export default PersonalScoringCriteriaModal;
